import { Instance, SnapshotIn, applySnapshot, cast, getSnapshot, types } from 'mobx-state-tree';
import { polygon, area } from '@turf/turf';
import { isPresent } from '@farmai-shared/LFUtils';
import { LFMapGeoJSON, DEFAULT_CRS } from '@app/_components/lf-map/mapTypes';
import { DateTimeType } from './DateTimeUtility';
import { DateTime } from 'luxon';
export type SimplePolygonType = {
  type: 'Polygon';
  coordinates: number[][][];
};
export type SimplePointType = {
  type: 'Point';
  coordinates: [number, number];
};
export const Polygon = types.model({
  type: types.literal('Polygon'),
  coordinates: types.array(types.array(types.array(types.number)))
}).views(self => ({
  get outerCoords() {
    const coordinates = getSnapshot(self.coordinates);
    return coordinates?.[0]?.map(c => c);
  },
  get flatCoords() {
    return self.coordinates.flat().flat();
  },
  get valid() {
    return isPresent(self.coordinates) && this.outerCoords.length > 3;
  },
  get canRemovePoint() {
    return this.outerCoords && this.outerCoords.length > 4; // don't allow editable polygon smaller than 3 points for now
  },
  get areaFromPolygon() {
    if (isPresent(self.coordinates)) {
      return area(self);
    }
    return null;
  },
  get toJSON() {
    return {
      type: self.type,
      coordinates: self.coordinates.toJSON()
    };
  },
  get openOuterCoords() {
    const coordinates = getSnapshot(self.coordinates)?.[0];
    return coordinates.slice(0, -1).map(c => c);
  }
})).actions(self => ({
  replaceRing(ring: number, coords: number[][]) {
    const coordinates = self.coordinates;
    if (!isPresent(coords) && coordinates) {
      return coordinates.splice(ring, 1);
    }
    coordinates[ring] = cast(coords);
  },
  replaceCoords(coords: number[][][]) {
    self.coordinates = cast(coords);
  },
  removePoint(ring: number, idx: number) {
    let coords = self.coordinates[ring];
    coords?.splice(idx, 1);
    if (idx === 0) {
      coords?.splice(coords.length - 1, 1);
      coords?.push([...coords[0]]);
    }
    coords = coords.length < 4 && ring !== 0 ? cast([]) : coords;
    coords && this.replaceRing(ring, coords);
  },
  updateCoords(coords: number[], ring: number, idx: number) {
    const coordinates = self.coordinates[ring];
    if (coordinates) {
      coordinates[idx] = cast(coords);
      if (idx === 0) {
        coordinates[coordinates?.length - 1] = cast(coords);
      }
      this.replaceRing(ring, coordinates);
    }
  },
  findIndexFromOuterRing(coord: number[]) {
    for (let i = 0; i < self.outerCoords.length; i++) {
      if (self.outerCoords[i]?.[0] === coord[0] && self.outerCoords[i]?.[1] === coord[1]) {
        return i + 1;
      }
    }
  },
  splicePoint(coord: number[], idx: number) {
    const coords = self.outerCoords;
    coords.splice(idx, 0, coord);
    coords && this.replaceRing(0, coords);
  },
  addPoint(lng: number, lat: number) {
    if (self.coordinates) {
      const coordinates = self.outerCoords || [];
      coordinates.push([lng, lat]);
      applySnapshot(self.coordinates, [coordinates]);
    }
  },
  closePolygon() {
    if (self.coordinates) {
      const coordinates = self.outerCoords || [];
      if (coordinates.length >= 3) {
        coordinates.push(coordinates[0]);
        applySnapshot(self.coordinates, [coordinates]);
      }
    }
  },
  popLastPoint() {
    if (self.coordinates) {
      const coordinates = self.outerCoords || [];
      coordinates.pop();
      applySnapshot(self.coordinates, [coordinates]);
    }
  }
}));
export type IPolygon = Instance<typeof Polygon>;
export type IPolygonSnapshotIn = SnapshotIn<typeof Polygon>;
export const Point = types.model({
  type: types.literal('Point'),
  coordinates: types.array(types.number)
}).views(self => ({
  get lngLat() {
    return {
      latitude: self.coordinates[1],
      longitude: self.coordinates[0]
    };
  }
}));
export type IPoint = Instance<typeof Point>;
export type IPointSnapshotIn = SnapshotIn<typeof Point>;
export const GeometryCollection = types.model({
  type: types.literal('GeometryCollection'),
  geometries: types.array(types.union(Point, Polygon))
}).views(self => ({
  get hasPoint() {
    return self.geometries.some(geo => geo.type === 'Point');
  },
  get hasPolygon() {
    return self.geometries.some(geo => geo.type === 'Polygon');
  }
})).views(self => ({
  get getPoint() {
    if (self.hasPoint) {
      return self.geometries.find(geo => geo.type === 'Point') as IPoint;
    }
  },
  get getPolygon() {
    if (self.hasPolygon) {
      return self.geometries.find(geo => geo.type === 'Polygon') as IPolygon;
    }
  }
}));
export type IGeometryCollection = Instance<typeof GeometryCollection>;
export const AnyJsonValue = types.union(types.string, types.number, types.integer, types.Date, types.boolean, types.map(types.late(() => AnyJsonValue)), types.array(types.late(() => AnyJsonValue)), types.undefined, types.null, DateTimeType);
export type AnyJSONValue = Instance<typeof AnyJsonValue>;

// any model using GeoJSON needs to be wrapped with `withDateProcessors`
export const GeoJSON = types.model({
  id: types.identifier,
  type: 'Feature',
  geometry: GeometryCollection,
  properties: types.map(types.maybeNull(AnyJsonValue))
}).views(self => ({
  get getPolygon() {
    if (self.geometry) return self.geometry.getPolygon;
  },
  get getPoint() {
    if (self.geometry.hasPoint) return self.geometry.getPoint;
  },
  get getProperties() {
    return self.properties.toJSON();
  },
  get name() {
    return self.properties.get('name') as string;
  },
  get area() {
    return self.properties.get('area') as number;
  },
  get note() {
    return self.properties.get('note') as string | null;
  },
  get createdAt(): DateTime {
    return self.properties.get('createdAt');
  },
  get farmId() {
    return self.properties.get('farm_id');
  },
  get fieldArea() {
    return this.getPolygon?.areaFromPolygon || null;
  },
  get selectedArea() {
    return self.properties.get('selectedArea');
  },
  get timezone() {
    return self.properties.get('timezone');
  },
  get previewArea(): number | null {
    const polygonGeom = self.geometry.getPolygon;
    if (!polygonGeom) return null;
    const outerCoords = getSnapshot(polygonGeom.coordinates)?.[0];
    if (!outerCoords || outerCoords.length < 2) return null;
    try {
      const firstCoord = outerCoords[0];
      const lastCoord = outerCoords[outerCoords.length - 1];
      const isClosed = firstCoord[0] === lastCoord[0] && firstCoord[1] === lastCoord[1];
      const closedCoords = isClosed ? outerCoords : [...outerCoords, firstCoord];
      if (closedCoords.length < 4) return null;
      const turfPoly = polygon([closedCoords]);
      return area(turfPoly);
    } catch {
      return null;
    }
  },
  get mapPoly(): LFMapGeoJSON | undefined {
    // currently tied to how the map handles polygons
    if (!this.getPolygon) return undefined;
    return {
      geometry: getSnapshot(this.getPolygon),
      properties: {
        centroid: this.getPoint?.coordinates || [],
        cursor: 'default',
        scaleTargetId: `map-scale-${self.id}`,
        crs: DEFAULT_CRS,
        key: `polygon_${self.id}`,
        name: this.name,
        cropzoneID: self.id
      }
    };
  },
  get validPolygon() {
    return this.getPolygon?.valid || false;
  }
})).actions(self => ({
  setProperty(key: string, value: AnyJSONValue) {
    self.properties.set(key, value);
  },
  updateCoords(coords: number[], ring: number, idx: number) {
    if (!self.geometry.hasPolygon) return;
    self.getPolygon!.updateCoords(coords, ring, idx);
  },
  findIndexFromOuterRing(coord: number[]) {
    if (!self.geometry.hasPolygon) return;
    return self.getPolygon!.findIndexFromOuterRing(coord);
  },
  splicePoint(coord: number[], idx: number) {
    if (!self.geometry.hasPolygon) return;
    self.getPolygon!.splicePoint(coord, idx);
  },
  removePoint(ring: number, idx: number) {
    if (!self.geometry.hasPolygon) return;
    self.getPolygon!.removePoint(ring, idx);
  },
  replaceCoords(coords: number[][][]) {
    if (!self.geometry.hasPolygon) return;
    self.getPolygon!.replaceCoords(coords);
  }
}));
export type IGeoJSON = Instance<typeof GeoJSON>;
export type IGeoJSONSnapshotIn = SnapshotIn<typeof GeoJSON>;